import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'store';
import { selectDsmEdits } from '../RoofDataSlice/roofSelector';
import { hasPendingChanges, type FlattenShape } from './index';

const treeState = (state: RootState) => state.treeSlice.data;

// ================== TREE SELECTOR ====================
// Derived render state only. Kept narrow on purpose: the previous version
// spread `...treeState`, so its output identity changed on ANY tree field
// (loading flags, history, currentIndex, is3DEnabled, …) and every consumer
// re-rendered. Read plain flags via the primitive selectors below instead.
const selectTreeState = createSelector(
	[selectDsmEdits, treeState], (savedEdits, treeState) => {
		const trees = [
			...Object.values(treeState.trees.added),
			...savedEdits.trees.map(t => ({
				...t,
				...(treeState.trees.updated[(t.id as number)] ?? {})

			}))
		].filter(t=> !treeState.deleted.includes(t.id as number));

		const selectedTree = trees.find(t => t.id === treeState.selectedShapeId) ?? null;
		return {
			trees,
			selectedTree,
			selectedTreeId: selectedTree?.id ?? null
		};
	}
);

// ================== PRIMITIVE FLAG SELECTORS ====================
// Return primitives so useSelector's default `===` check skips re-renders even
// when the tree slice changes for unrelated reasons.
const selectTreeEditingMode = (state: RootState) => state.treeSlice.data.treeEditingMode;
const selectTreeDeleteMode = (state: RootState) => state.treeSlice.data.deleteMode;
const selectIsDrawingTree = (state: RootState) => state.treeSlice.data.isDrawingTree;
const selectIsDraftingTree = (state: RootState) => state.treeSlice.data.isDraftingTree;

// ==================== FLATTEN AREA SELECTOR ============================
const selectFlattenAreaState = createSelector(
	[selectDsmEdits, treeState], (savedEdits, treeState) => {
		const flattenAreaPolygons = [
			...Object.values(treeState.flattenArea.added),
			...savedEdits.flattenArea.map(p => ({
				...p,
				...(treeState.flattenArea.updated[(p.id as number)] ?? {})
			}))
		].filter(p=> !treeState.deleted.includes(p.id as number));
		const activePolygon = flattenAreaPolygons.find(p => p.id === treeState.selectedShapeId) ?? null;
		const flattenAreaShapeById: Record<string, FlattenShape> = {};
		for (const polygon of flattenAreaPolygons) {
			const key = String(polygon.id);
			flattenAreaShapeById[key] = polygon.shape ?? (polygon.coords?.length === 32 ? 'circle' : 'polygon');
		}

		return {
			flattenAreaPolygons,
			activePolygon,
			activePolygonID: activePolygon?.id ?? null,
			flattenAreaShapeById,
		};
	}
);

// Primitive flatten flags — read these instead of pulling them off the merged
// object above, so flag-only consumers don't re-render when polygons recompute.
const selectIsFlattenAreaMode = (state: RootState) => state.treeSlice.data.isFlattenAreaMode;
const selectFlattenShape = (state: RootState) => state.treeSlice.data.flattenShape;

const selectHasTreeEdits = createSelector(
	[treeState],
	(treeState) =>
		hasPendingChanges(treeState.trees) ||
		hasPendingChanges(treeState.flattenArea) ||
		treeState.deleted.length > 0
);

const selectTreeDoneLoading = (state: RootState) => state.treeSlice.data.isTreeDoneLoading;

// True while any flatten area's API call is in flight.
const selectIsFlattenLoading = (state: RootState) =>
	Object.keys(state.treeSlice.data.loadingFlattenIds).length > 0;

export {
	selectTreeState,
	selectFlattenAreaState,
	selectHasTreeEdits,
	selectTreeDoneLoading,
	selectIsFlattenLoading,
	selectTreeEditingMode,
	selectTreeDeleteMode,
	selectIsDrawingTree,
	selectIsDraftingTree,
	selectIsFlattenAreaMode,
	selectFlattenShape,
};
